import {
  asProfilePost,
  createProfileSchema,
  emptyProfileSchema,
  fromProfileFields,
} from '@meprism/shared/src/schemas/ProfileForm'
import {
  BaseProfileState,
  FetchProfile,
  PostProfile,
} from '@meprism/shared/src/redux/profile/ProfileSlice'
import { BaseThunkExtra } from '@meprism/shared'
import {
  useForm,
  UseFormProps,
  SubmitHandler,
  useFieldArray,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Logger } from '@meprism/app-utils'
import { useDispatch } from 'react-redux'
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit'
import { z } from 'zod'
import { useFormData } from './FormDataHelper'
import { normalizeMiddleName } from '../helpers'
import { selectActiveGrant } from '../../redux/product/ProductSlice'
import { useAppSelector } from '../../redux/storeExports'
import { logFirebaseEvent } from '../firebaseConfig'
import AnalyticsManager from '../../services/AnalyticsManager'
import { logCustomMetapixelEvent } from '../MetapixelManager'
const profileSchema = createProfileSchema(false, false, false, false)

export type ProfileSchemaType = z.TypeOf<typeof profileSchema>

export type ProfileFormHookReturn = ReturnType<
  typeof useForm<ProfileSchemaType>
>

export interface UseProfileFormProps {
  form?: UseFormProps<ProfileSchemaType>
  onSubmitSuccess?: () => void
  onSubmitError?: (error: any) => void
  linkedinOptional?: boolean
  streetOptional?: boolean
  zipOptional?: boolean
  phoneNumberOptional?: boolean
  agreeToTermsOptional?: boolean | undefined
}

export const useProfileForm = (props: UseProfileFormProps) => {
  const grant = useAppSelector(selectActiveGrant)
  const { setFormValue } = useFormData()
  const dispatch =
    useDispatch<ThunkDispatch<BaseProfileState, BaseThunkExtra, AnyAction>>()
    const getDefaultValues = async () => {
      try {
        const profile = await dispatch(FetchProfile()).unwrap()
        const defaultValues = fromProfileFields({
          ...profile,
          additional_information: profile.additional_information || {
            additional_first_name: [],
            maiden_name: []
          },
          tour_state: profile.tour_state,
          last_scan_initiated_at: profile.last_scan_initiated_at,
          metadata: profile.metadata,
        })
        if (!defaultValues.phoneNumber || defaultValues.phoneNumber.length === 0) {
          defaultValues.phoneNumber = [{ value: '' }];
        }
        return defaultValues
      } catch (error) {
        Logger.error(`Error fetching profile: ${error}`)
        return emptyProfileSchema()
      }
    }

  const submitHandler: SubmitHandler<ProfileSchemaType> = async (values, event) => {
    const isOnBoardingRequest = (event as any)?._is_on_boarding_request ?? false;
    try {
      const payload = asProfilePost({
        ...values,
        phoneNumber: values.phoneNumber ?? [],
        agreeToTerms: !!values.agreeToTerms,
        middleName: values.middleName.map(m => ({value: normalizeMiddleName(m.value)})),
      },
    )

      await dispatch(PostProfile({
        ...payload,
        isOnBoardingRequest,
        ...(isOnBoardingRequest && {
          metadata: {
            ...(payload.metadata || {}),
            onboarding_step: 2,
          },
        }),
      }))
      if(isOnBoardingRequest){
        AnalyticsManager.trackTypedEvent({event: 'Profile Saved - Web', source: 'Web App'})
        if(!grant){
          AnalyticsManager.trackTypedEvent({event: 'Onboarding Completed - Web', source: "Web App"})
          logFirebaseEvent('Onboarding Completed - Web')
          logCustomMetapixelEvent('Onboarding Completed - Web')
        }
      }

      // Set form data to the context
      setFormValue('profile', values) // Use the correct key and data

      if (props.onSubmitSuccess) {
        props.onSubmitSuccess()
      }
    } catch (error) {
      Logger.error(`Error submitting profile update: ${error}`)
      if (props.onSubmitError) {
        props.onSubmitError(error)
      }
    }
  }

  const {
    register,
    setValue,
    watch,
    setFocus,
    formState: { errors, isSubmitting, isValid, isLoading },
    control,
    handleSubmit,
    trigger,
    setError,
  } = useForm<ProfileSchemaType>({
    mode: 'onChange',
    resolver: zodResolver(
      createProfileSchema(
        Boolean(props.linkedinOptional),
        Boolean(props.streetOptional),
        Boolean(props.phoneNumberOptional),
        Boolean(props.agreeToTermsOptional),
        Boolean(props.zipOptional),
      ),
    ),
    defaultValues: getDefaultValues,
  })

  const firstNameArray = useFieldArray({
    control,
    name: 'firstName' as const,
  })

  const otherFirstNameArray = useFieldArray({
    control,
    name: 'otherFirstName' as const,
  })

  const otherMaidenNameArray = useFieldArray({
    control,
    name: 'otherMaidenName' as const,
  })

  const middleNameArray = useFieldArray({
    control,
    name: 'middleName' as const,
  })

  const lastNameArray = useFieldArray({
    control,
    name: 'lastName' as const,
  })

  const addressArray = useFieldArray({
    control,
    name: 'address' as const,
  })

  const emailArray = useFieldArray({
    control,
    name: 'email' as const,
  })

  const phoneNumberArray = useFieldArray({
    control,
    name: 'phoneNumber' as const,
  })

  return {
    register,
    setValue,
    watch,
    errors,
    isSubmitting,
    isLoading,
    setFocus,
    control,
    handleSubmit,
    submitHandler,
    isValid,
    firstNameArray,
    middleNameArray,
    lastNameArray,
    otherFirstNameArray,
    otherMaidenNameArray,
    addressArray,
    emailArray,
    phoneNumberArray,
    trigger,
    setError,
  }
}
